<template>
  <v-dialog v-model="visible" fullscreen transition="slide-x-reverse-transition" class="station-switch-dialog"
    :scrim="true" scrollable>
    <div class="d-flex w-100 h-100 sidebar-wrapper">
      <div class="flex-grow-1" @click="visible = false"></div>

      <div class="dialog-container">
        <div class="dialog-header">
          <div class="dialog-title">电站查询</div>
          <v-btn icon="mdi-close" variant="text" @click="visible = false" class="close-btn"></v-btn>
        </div>

        <div class="dialog-content">
          <div class="filter-section">
            <div class="filter-toolbar station-search">
              <div class="filter-item keyword-item">
                <span v-if="!searchParams.keyword" class="visible-placeholder">电站名称 / ID</span>
                <v-text-field v-model="searchParams.keyword" placeholder="" variant="plain" density="compact"
                  hide-details class="filter-control" @update:modelValue="handleKeywordSearch"
                  @keyup.enter="handleSearch" clearable @click:clear="handleSearch">
                  <template #append-inner>
                    <v-icon icon="mdi-magnify" size="small" class="search-icon" @click="handleSearch" />
                  </template>
                </v-text-field>
              </div>

              <div class="filter-item region-item">
                <span v-if="!searchParams.province && !provinceKeyword" class="visible-placeholder">省份</span>
                <v-autocomplete v-model="searchParams.province" v-model:search="provinceKeyword" :items="provinceList"
                  placeholder="" persistent-placeholder variant="plain" density="compact" hide-details hide-no-data
                  class="filter-control" clearable :custom-filter="regionFuzzyFilter"
                  @update:model-value="onProvinceChange" @click:clear="clearProvince"
                  @keydown.enter.prevent="handleRegionEnter('province')" />
              </div>

              <div class="filter-item region-item" :class="{ disabled: !searchParams.province }">
                <span v-if="!searchParams.city && !cityKeyword" class="visible-placeholder">城市</span>
                <v-autocomplete v-model="searchParams.city" v-model:search="cityKeyword" :items="cityList"
                  placeholder="" persistent-placeholder variant="plain" density="compact" hide-details hide-no-data
                  class="filter-control" clearable :disabled="!searchParams.province" :custom-filter="regionFuzzyFilter"
                  @update:model-value="handleSearch" @click:clear="clearCity"
                  @keydown.enter.prevent="handleRegionEnter('city')" />
              </div>

              <v-menu v-model="advancedFilterMenu" :close-on-content-click="false" location="bottom end" offset="8">
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="outlined" class="filter-action-btn">
                    更多筛选
                    <span v-if="activeAdvancedFilterCount" class="filter-count">{{ activeAdvancedFilterCount }}</span>
                  </v-btn>
                </template>

                <div class="advanced-filter-panel">
                  <div class="advanced-filter-title">更多筛选</div>

                  <div class="panel-field">
                    <div class="panel-field-label">装机容量</div>
                    <div class="panel-control-wrap">
                      <span v-if="!capacityRange && !capacityKeyword" class="field-placeholder">请选择装机容量</span>
                      <v-autocomplete v-model="capacityRange" v-model:search="capacityKeyword" :items="capacityOptions"
                        item-title="title" item-value="value" placeholder="" persistent-placeholder variant="outlined"
                        density="compact" hide-details hide-no-data clearable class="panel-control"
                        @update:model-value="handleCapacityChange" @click:clear="clearCapacity" />
                    </div>
                  </div>

                  <div class="panel-field">
                    <div class="panel-field-label">设备型号</div>
                    <div class="panel-control-wrap">
                      <span v-if="!searchParams.eBlockModel && !eBlockModelKeyword"
                        class="field-placeholder">请选择设备型号</span>
                      <v-autocomplete v-model="searchParams.eBlockModel" v-model:search="eBlockModelKeyword"
                        :items="appStore.eBlockModels" item-title="modelName" item-value="modelNumber" placeholder=""
                        persistent-placeholder variant="outlined" density="compact" hide-details hide-no-data clearable
                        class="panel-control" @update:model-value="handleSearch" @click:clear="clearEBlockModel" />
                    </div>
                  </div>
                </div>
              </v-menu>

              <v-btn variant="outlined" class="reset-btn" prepend-icon="mdi-refresh" @click="resetFilters()">重置</v-btn>
            </div>
          </div>

          <!-- 表格区域 -->
          <div class="table-section">
            <v-table class="station-table" hover fixed-header>
              <thead>
                <tr>
                  <th class="station-name-col">电站名称</th>
                  <th>省份</th>
                  <th>
                    城市/区域
                  </th>
                  <th class="sortable-th" @click="toggleSort('online')">
                    <span class="th-sort-content">
                      实时状态
                      <v-icon :icon="getSortIcon('online')" size="16" class="sort-icon" />
                    </span>
                  </th>
                  <th>
                    <span class="th-two-line">设备品牌<span>设备型号</span></span>
                  </th>
                  <th>
                    <span class="th-two-line">投运日期<span>安全运行天数</span></span>
                  </th>
                  <th class="sortable-th" @click="toggleSort('stationStatus')">
                    <span class="th-sort-content">
                      电站状态
                      <v-icon :icon="getSortIcon('stationStatus')" size="16" class="sort-icon" />
                    </span>
                  </th>
                  <th>行业</th>
                  <th>应用类别</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in sortedDataList" :key="item?.stationId || index"
                  @click="onStationSelect(item)" class="cursor-pointer">
                  <td class="station-name-cell">
                    <div class="station-name">
                      {{ item?.name || '-' }}
                    </div>
                    <div class="station-meta">ID：{{ item?.stationId ?? '-' }}</div>
                    <div class="station-capacity">{{ formatPowerDisplay(item?.maxPower) }} / {{
                      formatCapacityDisplay(item?.capacity) }}</div>
                  </td>
                  <td style="white-space: nowrap;">{{ item?.province || '-' }}</td>
                  <td>{{ item?.city || '-' }}{{ item?.area ? '/' + item.area : '' }}</td>
                  <td>
                    <v-chip variant="outlined" size="small" class="comm-status"
                      :class="item?.online ? 'online' : 'offline'" :color="item?.online ? '#33BE36' : '#6B6C6D'"
                      :prepend-icon="item?.online ? 'mdi-wifi' : 'mdi-wifi-off'">
                      {{ item?.online ? '在线' : '离线' }}
                    </v-chip>
                  </td>
                  <td>
                    <v-tooltip location="top" :disabled="getDeviceModelCount(item) <= 1">
                      <template v-slot:activator="{ props }">
                        <div class="device-cell" v-bind="props">
                          <div v-if="item?.deviceBrand && item.deviceBrand !== '-'" class="device-brand">
                            {{ item.deviceBrand }}
                          </div>
                          <div class="device-model-row">
                            <span class="device-model-name">{{ getPrimaryDeviceModel(item) }}</span>
                            <span v-if="getDeviceModelCount(item) > 1" class="device-model-count">
                              +{{ getDeviceModelCount(item) - 1 }}
                            </span>
                          </div>
                        </div>
                      </template>
                      <span>{{ getDeviceModelText(item) }}</span>
                    </v-tooltip>
                  </td>
                  <td>
                    <div>{{ formatDate(item?.commossioningTime) }}</div>
                    <div style="margin-top: 2px;">{{ calculateRunDays(item?.commossioningTime) }}天</div>
                  </td>
                  <td>
                    <v-chip size="small" label variant="tonal" class="station-status-chip"
                      :color="getStationStatusColor(item)">
                      {{ getStationStatusText(item) }}
                    </v-chip>
                  </td>
                  <td>{{ item?.industry || '-' }}</td>
                  <td>{{ item?.applicationCategory || '-' }}</td>
                </tr>
                <tr v-if="(!dataList || dataList.length === 0) && !loading">
                  <td colspan="9" class="text-center py-8 text-grey">暂无数据</td>
                </tr>
                <tr v-if="loading">
                  <td colspan="9" class="text-center py-8">
                    <v-progress-circular indeterminate color="#3a68ff"></v-progress-circular>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <!-- 分页 -->
          <div class="pagination-section mt-4">
            <TablePagination v-model="page" v-model:page-size="pageSize" :total="total" :has-sizes="true"
              :has-jump="true" @change="handlePageChange" />
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getMyStationList } from '@/api/home'
import { regionData } from '@/utils/regionData'
import useAppStore from '@/store/modules/app'
import TablePagination from '@/components/TablePagination/index.vue'

const router = useRouter()
const route = useRoute()
const appStore = useAppStore()
const visible = ref(false)
const loading = ref(false)
const advancedFilterMenu = ref(false)

const capacityRange = ref(null)
const capacityKeyword = ref('')
const provinceKeyword = ref('')
const cityKeyword = ref('')
const eBlockModelKeyword = ref('')
const searchParams = ref({
  keyword: '',
  province: '',
  city: '',
  area: '',
  eBlockModel: '',
  startCapacity: null,
  endCapacity: null
})

const capacityOptions = [
  { title: '小于1MWh', value: '<1' },
  { title: '1 - 5MWh', value: '1-5' },
  { title: '5 - 10MWh', value: '5-10' },
  { title: '10 - 20MWh', value: '10-20' },
  { title: '大于20MWh', value: '>20' }
]

let searchTimeout = null

const handleKeywordSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    handleSearch()
  }, 500)
}

const provinceList = computed(() => Object.keys(regionData))
const cityList = computed(() => {
  if (!searchParams.value.province) return []
  return regionData[searchParams.value.province] || []
})

const findMatchedRegion = (keyword, list) => {
  const value = String(keyword || '').trim()
  if (!value) return ''
  return list.find(item => item === value) || list.find(item => item.includes(value)) || ''
}

/** 省市下拉：输入关键字模糊匹配（包含） */
const regionFuzzyFilter = (value, query, item) => {
  if (query == null || String(query).trim() === '') return true
  const text = String(value ?? item?.raw ?? '').toLowerCase()
  return text.includes(String(query).toLowerCase().trim())
}

/** 装机容量：接口 kWh；≥1MWh 显示 MWh，否则 kWh */
const formatCapacityDisplay = (kwh) => {
  const n = Number(kwh)
  if (!Number.isFinite(n)) return '-'
  if (n >= 1000) {
    const mwh = n / 1000
    const s = Number.isInteger(mwh) ? String(mwh) : String(parseFloat(mwh.toFixed(3))).replace(/\.?0+$/, '')
    return `${s}MWh`
  }
  const s = Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(2))).replace(/\.?0+$/, '')
  return `${s}kWh`
}

/** 额定功率：接口 kW；≥1MW 显示 MW，否则 kW */
const formatPowerDisplay = (kw) => {
  const n = Number(kw)
  if (!Number.isFinite(n)) return '-'
  if (n >= 1000) {
    const mw = n / 1000
    const s = Number.isInteger(mw) ? String(mw) : String(parseFloat(mw.toFixed(3))).replace(/\.?0+$/, '')
    return `${s}MW`
  }
  const s = Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(2))).replace(/\.?0+$/, '')
  return `${s}kW`
}

const onProvinceChange = () => {
  searchParams.value.city = ''
  cityKeyword.value = ''
  handleSearch()
}

const clearProvince = () => {
  searchParams.value.province = ''
  provinceKeyword.value = ''
  clearCity()
}

const clearCity = () => {
  searchParams.value.city = ''
  cityKeyword.value = ''
  handleSearch()
}

const clearCapacity = () => {
  capacityRange.value = null
  capacityKeyword.value = ''
  searchParams.value.startCapacity = null
  searchParams.value.endCapacity = null
  handleSearch()
}

const clearEBlockModel = () => {
  searchParams.value.eBlockModel = ''
  eBlockModelKeyword.value = ''
  handleSearch()
}

const handleRegionEnter = (type) => {
  if (type === 'province') {
    const matchedProvince = findMatchedRegion(provinceKeyword.value || searchParams.value.province, provinceList.value)
    if (matchedProvince && matchedProvince !== searchParams.value.province) {
      searchParams.value.province = matchedProvince
      searchParams.value.city = ''
      cityKeyword.value = ''
    }
  } else if (type === 'city') {
    const matchedCity = findMatchedRegion(cityKeyword.value || searchParams.value.city, cityList.value)
    if (matchedCity) searchParams.value.city = matchedCity
  }
  handleSearch()
}

const page = ref(1)
const pageSize = ref(10)
const total = ref(0)
const dataList = ref([])
const sortState = ref({
  key: '',
  order: ''
})

const stationStatusMap = {
  0: { text: '已投运', color: 'success', order: 1 },
  1: { text: '未投运', color: 'warning', order: 2 },
  2: { text: '暂停运营', color: 'error', order: 3 },
  3: { text: '待审核', color: 'info', order: 4 },
  4: { text: '被驳回', color: 'error', order: 5 }
}

const stationStatusTextOrder = {
  已投运: 1,
  未投运: 2,
  暂停运营: 3,
  待审核: 4,
  被驳回: 5
}

const sortedDataList = computed(() => {
  const list = Array.isArray(dataList.value) ? [...dataList.value] : []
  const { key, order } = sortState.value
  if (!key || !order) return list

  const direction = order === 'asc' ? 1 : -1
  return list.sort((a, b) => {
    const aValue = getSortValue(a, key)
    const bValue = getSortValue(b, key)
    if (aValue === bValue) return 0
    return aValue > bValue ? direction : -direction
  })
})

const activeAdvancedFilterCount = computed(() => {
  let count = 0
  if (capacityRange.value) count += 1
  if (searchParams.value.eBlockModel) count += 1
  return count
})

let menuName = ''

const resetFilters = (shouldSearch = true) => {
  capacityRange.value = null
  searchParams.value = {
    keyword: '',
    province: '',
    city: '',
    area: '',
    eBlockModel: '',
    startCapacity: null,
    endCapacity: null
  }
  provinceKeyword.value = ''
  cityKeyword.value = ''
  capacityKeyword.value = ''
  eBlockModelKeyword.value = ''
  sortState.value = { key: '', order: '' }
  page.value = 1
  advancedFilterMenu.value = false
  if (shouldSearch) fetchData()
}

const open = (...args) => {
  menuName = args[0] ?? ''
  if (!appStore.modelList?.length) {
    // appStore.getModelList()
  }
  resetFilters(false)
  visible.value = true
  fetchData()
}

defineExpose({ open })

const handleCapacityChange = (val) => {
  capacityKeyword.value = ''
  if (val === '<1') {
    searchParams.value.startCapacity = null
    searchParams.value.endCapacity = 1000 // 1MWh = 1000kWh
  } else if (val === '1-5') {
    searchParams.value.startCapacity = 1000
    searchParams.value.endCapacity = 5000
  } else if (val === '5-10') {
    searchParams.value.startCapacity = 5000
    searchParams.value.endCapacity = 10000
  } else if (val === '10-20') {
    searchParams.value.startCapacity = 10000
    searchParams.value.endCapacity = 20000
  } else if (val === '>20') {
    searchParams.value.startCapacity = 20000
    searchParams.value.endCapacity = null
  } else {
    searchParams.value.startCapacity = null
    searchParams.value.endCapacity = null
  }
  handleSearch()
}

const handleSearch = () => {
  page.value = 1
  fetchData()
}

const handlePageChange = (newPage) => {
  page.value = newPage
  fetchData()
}

const toggleSort = (key) => {
  const current = sortState.value
  if (current.key !== key) {
    sortState.value = { key, order: 'asc' }
    return
  }
  if (current.order === 'asc') {
    sortState.value = { key, order: 'desc' }
    return
  }
  sortState.value = { key: '', order: '' }
}

const getSortIcon = (key) => {
  if (sortState.value.key !== key || !sortState.value.order) return 'mdi-swap-vertical'
  return sortState.value.order === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'
}

const getStationStatusText = (station) => {
  const state = station?.stationState
  if (state !== undefined && state !== null && stationStatusMap[state]) {
    return stationStatusMap[state].text
  }
  return station?.status || '已投运'
}

const getStationStatusColor = (station) => {
  const state = station?.stationState
  if (state !== undefined && state !== null && stationStatusMap[state]) {
    return stationStatusMap[state].color
  }
  const statusText = getStationStatusText(station)
  if (statusText === '未投运') return 'warning'
  if (statusText === '暂停运营' || statusText === '被驳回') return 'error'
  if (statusText === '待审核') return 'info'
  return 'success'
}

const getSortValue = (station, key) => {
  if (key === 'online') return station?.online ? 1 : 0
  if (key === 'stationStatus') {
    const state = station?.stationState
    if (state !== undefined && state !== null && stationStatusMap[state]) {
      return stationStatusMap[state].order
    }
    return stationStatusTextOrder[getStationStatusText(station)] || 99
  }
  return 0
}

const getDeviceModelList = (station) => {
  const value = station?.eBlockModel
  if (!value) return []
  return String(value)
    .split(/[,，、]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

const getDeviceModelText = (station) => {
  const brand = station?.deviceBrand && station.deviceBrand !== '-' ? station.deviceBrand : ''
  const models = getDeviceModelList(station).join('，')
  return [brand, models].filter(Boolean).join(' / ') || '-'
}

const getPrimaryDeviceModel = (station) => {
  return getDeviceModelList(station)[0] || '-'
}

const getDeviceModelCount = (station) => {
  return getDeviceModelList(station).length
}

let fetchDataSeq = 0

const stationKeywordParams = () => {
  const keyword = String(searchParams.value.keyword || '').trim()
  if (!keyword) return {}
  if (/^\d+$/.test(keyword)) return { stationId: keyword }
  return { name: keyword }
}

const fetchData = async () => {
  const seq = ++fetchDataSeq
  loading.value = true
  try {
    const params = {
      pageNum: page.value,
      pageSize: pageSize.value,
      ...stationKeywordParams()
    }

    if (searchParams.value.province) params.province = searchParams.value.province
    if (searchParams.value.city) params.city = searchParams.value.city
    if (searchParams.value.area) params.area = searchParams.value.area
    if (searchParams.value.startCapacity !== null && searchParams.value.startCapacity !== '') {
      params.startCapacity = Number(searchParams.value.startCapacity)
    }
    if (searchParams.value.endCapacity !== null && searchParams.value.endCapacity !== '') {
      params.endCapacity = Number(searchParams.value.endCapacity)
    }
    if (searchParams.value.eBlockModel) {
      params.eBlockModel = searchParams.value.eBlockModel
    }

    const res = await getMyStationList(params)
    if (seq !== fetchDataSeq) return
    if (res.code === 200 && res.data) {
      // 修复 list 为 null 时的处理逻辑
      let list = Array.isArray(res.data.list) ? res.data.list : []
      if (!res.data.list && Array.isArray(res.data)) {
        list = res.data
      }
      dataList.value = list
      total.value = res.data.total || list.length
    } else {
      dataList.value = []
      total.value = 0
    }
  } catch (error) {
    if (seq !== fetchDataSeq) return
    console.error('Failed to fetch station list:', error)
    dataList.value = []
    total.value = 0
  } finally {
    if (seq === fetchDataSeq) loading.value = false
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const calculateRunDays = (dateStr) => {
  if (!dateStr) return '-'
  const start = new Date(dateStr.split(' ')[0])
  if (isNaN(start.getTime())) return '-'
  const now = new Date()
  // 忽略时分秒，只计算日期的差值
  const diffTime = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
    new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return diffDays >= 0 ? diffDays : '-'
}

function getDetailPath(tab) {
  if (route.path === '/it-operations/station/add') {
    return '/it-operations/station/add'
  }

  const list = [
    'index',
    'my-powerStation',
    'system-manager',
  ]

  if ([
    '收益总览',
    '电价录入',
    '抄表结算',
    '策略控制',
  ].includes(tab)) {
    return '/business-operations/detail'
  }
  if (['告警信息', '数据分析'].includes(tab)) {
    return '/it-operations/detail'
  }

  // 一级路径
  const path_l1 = route.path.split('/')[1]
  if (list.includes(path_l1)) {
    return '/station-monitor/detail'
  } else {
    return '/' + path_l1 + '/detail'
  }

}


const onStationSelect = (station) => {
  if (!station) return
  const stationId = station.stationId

  if (stationId) {
    visible.value = false
    const isStationAddPage = route.path === '/it-operations/station/add'
    router.push({
      path: getDetailPath(menuName),
      query: isStationAddPage
        ? { stationId: String(stationId), step: 'basic' }
        : { stationId, tab: menuName }
    })
  }
}
</script>

<style lang="scss" scoped>
.station-switch-dialog {
  :deep(.v-overlay__content) {
    width: 100%;
    height: 100%;
    max-width: none !important;
    max-height: none;
    margin: 0;
    pointer-events: none;
    display: flex;
  }
}

.sidebar-wrapper {
  pointer-events: none;
}

.sidebar-wrapper>div:first-child {
  pointer-events: auto;
}

.dialog-container {
  width: 1225px;
  height: 100%;
  background-color: #fff;
  background-image: linear-gradient(180deg, #E6F3FF 0.11%, rgba(255, 255, 255, 0.00) 24.22%);
  border-radius: 24px 0 0 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  filter: drop-shadow(0 4px 30px rgba(0, 0, 0, 0.20));
  pointer-events: auto;
  margin-left: auto;
  /* Push to the right */
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px 0 0;
  margin: 0 0 16px 32px;
  border-bottom: 1px solid #CAD3F5;

  .dialog-title {
    font-size: 16px;
    font-weight: 500;
    color: #000;
  }

  .close-btn {
    color: #86909C;
  }
}

.dialog-content {
  padding: 0 32px 32px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.filter-section {
  flex-shrink: 0;
  background: transparent;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(229, 230, 235, 0.5);
}

.filter-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  flex-wrap: wrap;
}

.station-search {
  .filter-item {
    position: relative;
    height: 36px;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 10px 0 12px;
    background: #fff;
    border: 1px solid #d8deeb;
    border-radius: 18px;
    transition: border-color 0.16s ease, box-shadow 0.16s ease;
  }

  .filter-item:hover,
  .filter-item:focus-within {
    border-color: #8ea6ff;
    box-shadow: 0 0 0 2px rgba(58, 104, 255, 0.08);
  }

  .filter-item.disabled {
    background: rgba(247, 248, 250, 0.75);
    border-color: #e5e8ef;
  }

  .keyword-item {
    width: 235px;
    padding-left: 14px;
  }

  .region-item {
    width: 148px;
  }

  .filter-control {
    min-width: 0;
    flex: 1 1 auto;
  }

  .visible-placeholder {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #9aa3b2;
    font-size: 13px;
    font-family: inherit;
    font-weight: 400;
    line-height: 1;
    pointer-events: none;
    z-index: 1;
  }

  .filter-action-btn,
  .reset-btn {
    height: 32px;
    border-radius: 16px;
    font-size: 13px;
    font-weight: 400;
  }

  .filter-action-btn {
    min-width: 96px;
    padding: 0 12px;
    color: #3a68ff;
    border-color: #b8c5ff;
    background: #fff;
  }

  .reset-btn {
    min-width: 78px;
    padding: 0 12px;
    color: #4e5969;
    border-color: #d8deeb;
    background: #fff;
  }

  .filter-count {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    margin-left: 6px;
    border-radius: 9px;
    background: #3a68ff;
    color: #fff;
    font-size: 12px;
    line-height: 18px;
  }

  .search-icon {
    cursor: pointer;
  }

  :deep(.v-field) {
    min-height: 34px;
    padding: 0;
  }

  :deep(.v-field__input) {
    min-height: 34px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-inline: 0 !important;
    display: flex;
    align-items: center;
    font-size: 13px;
    font-family: inherit;
    font-weight: 400;
  }

  :deep(.v-select__selection) {
    margin-top: 0;
    align-items: center;
  }

  :deep(.v-select__selection-text) {
    line-height: 34px;
  }

  :deep(.v-field__input input) {
    align-self: center;
    flex: 1 1 auto;
    min-height: 34px;
    min-width: 0;
    width: 100%;
  }

  :deep(.v-field__input input) {
    margin-top: 0;
  }

  :deep(.v-field__input input::placeholder),
  :deep(.v-select__selection-text) {
    color: #9aa3b2;
    opacity: 1;
  }

  :deep(.v-field__clearable),
  :deep(.v-field__append-inner) {
    padding-top: 5px;
    align-items: center;
  }

  :deep(.v-field--variant-plain .v-field__overlay),
  :deep(.v-field--variant-plain .v-field__underlay) {
    display: none;
  }
}

.advanced-filter-panel {
  width: 300px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e5e8ef;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(29, 33, 41, 0.12);

  .advanced-filter-title {
    margin-bottom: 12px;
    color: #1d2129;
    font-size: 14px;
    font-weight: 500;
  }

  .panel-field {
    margin-top: 14px;
  }

  .panel-field:first-of-type {
    margin-top: 0;
  }

  .panel-field-label {
    margin-bottom: 6px;
    color: #4e5969;
    font-size: 13px;
    line-height: 18px;
  }

  .panel-control {
    width: 100%;
    background: #fff;
  }

  .panel-control-wrap {
    position: relative;
  }

  .field-placeholder {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #9aa3b2;
    font-size: 13px;
    font-family: inherit;
    font-weight: 400;
    line-height: 1;
    pointer-events: none;
    z-index: 1;
  }

  :deep(.v-field) {
    min-height: 36px;
    border-radius: 8px;
  }

  :deep(.v-field__input) {
    min-height: 36px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 13px;
  }

  :deep(.v-field__input input) {
    align-self: center;
    min-height: 34px;
  }

  :deep(.v-field__clearable),
  :deep(.v-field__append-inner) {
    padding-top: 6px;
    align-items: center;
  }
}

.table-section {
  background: #fff;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.station-table {
  background: #fff !important;
  height: 100%;
  display: flex;
  flex-direction: column;

  :deep(.v-table__wrapper) {
    flex: 1;
    overflow-y: auto;
  }

  th {
    background-color: #F7F8FA !important;
    color: #86909C !important;
    font-weight: 500 !important;
    font-size: 14px !important;
    white-space: nowrap;
    border-bottom: none !important;
    padding: 10px !important;
    line-height: 18px;
  }

  td {
    font-size: 14px;
    color: #1D2129;
    border-bottom: 1px solid #F2F3F5 !important;
    padding: 10px !important;
    background: transparent;
    vertical-align: middle;
  }

  tr:nth-child(odd) td {
    background: rgba(235, 240, 255, 0.50) !important;
  }

  tr:hover td {
    background-color: #F2F3F5 !important;
  }

  .station-name-col {
    min-width: 210px;
  }

  .th-two-line {
    display: inline-flex;
    flex-direction: column;
    gap: 2px;
    line-height: 16px;
  }

  .sortable-th {
    cursor: pointer;
    user-select: none;
  }

  .sortable-th:hover {
    color: #3a68ff !important;
  }

  .th-sort-content {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .sort-icon {
    color: currentColor;
    opacity: 0.76;
  }
}

.pagination-section {
  flex-shrink: 0;
}

.cursor-pointer {
  cursor: pointer;
}

.comm-status.online {
  background: #E8FFF3;
}

.comm-status.offline {
  background: #F4F9FF;
}

.station-name-cell {
  min-width: 210px;
}

.station-name {
  color: #1f4fff;
  font-weight: 600;
  line-height: 20px;
}

.station-meta {
  margin-top: 3px;
  color: #8a93a6;
  font-size: 12px;
  line-height: 16px;
}

.station-capacity {
  margin-top: 2px;
  color: #4e5969;
  font-size: 12px;
  line-height: 16px;
}

.station-status-chip {
  font-weight: 500;
}

.device-cell {
  max-width: 220px;
}

.device-brand {
  margin-bottom: 3px;
  color: #4e5969;
  font-size: 12px;
  line-height: 16px;
}

.device-model-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.device-model-name {
  min-width: 0;
  overflow: hidden;
  color: #1d2129;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-model-count {
  flex: 0 0 auto;
  height: 20px;
  min-width: 28px;
  padding: 0 7px;
  border-radius: 10px;
  background: #eef3ff;
  color: #3a68ff;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}
</style>
